/**
 * Client-side GPX 1.1 export (spec 03). Pure string building — the web layer
 * wraps the result in a Blob. Segments split on gaps > 2 min so a slept-screen
 * hole doesn't draw a straight line across the fjord.
 */
import type { TrackPoint } from './polyline.js';

const SEGMENT_SPLIT_MS = 120_000;

export interface GpxTrack {
  name: string;
  points: TrackPoint[];
}

function isoTime(ms: number): string {
  // Deterministic ISO 8601 UTC without relying on locale.
  return new Date(ms).toISOString();
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildGpx(track: GpxTrack): string {
  const segments: TrackPoint[][] = [];
  let current: TrackPoint[] = [];
  let prevTs: number | null = null;

  for (const p of track.points) {
    if (prevTs != null && p.ts - prevTs > SEGMENT_SPLIT_MS) {
      if (current.length) segments.push(current);
      current = [];
    }
    current.push(p);
    prevTs = p.ts;
  }
  if (current.length) segments.push(current);

  const segXml = segments
    .map((seg) => {
      const pts = seg
        .map(
          (p) =>
            `      <trkpt lat="${p.lat.toFixed(6)}" lon="${p.lon.toFixed(6)}">` +
            `<time>${isoTime(p.ts)}</time></trkpt>`,
        )
        .join('\n');
      return `    <trkseg>\n${pts}\n    </trkseg>`;
    })
    .join('\n');

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<gpx version="1.1" creator="LOS" xmlns="http://www.topografix.com/GPX/1/1">\n` +
    `  <trk>\n    <name>${escapeXml(track.name)}</name>\n${segXml}\n  </trk>\n` +
    `</gpx>\n`
  );
}
